import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import { Close } from '@mui/icons-material';

const ContactUs = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    projectCode: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Please enter name.';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Please enter email address.';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Please enter valid email address.';
      }
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Please enter message.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      let apiUrl = process.env.REACT_APP_API_URL || '';
      // Remove trailing slash if present
      apiUrl = apiUrl.replace(/\/$/, '');
      
      // Determine the correct endpoint
      const endpoint = apiUrl.includes('/api') ? '/contactus' : '/api/contactus';
      
      const response = await fetch(`${apiUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server returned non-JSON response');
      }

      const result = await response.json();

      if (result.success) {
        setSubmitMessage('Your message has been sent to Carson Administrator.');
        setFormData({
          name: '',
          email: '',
          projectCode: '',
          message: ''
        });
        
        setTimeout(() => {
          if (onClose) onClose();
        }, 3000);
      } else {
        setSubmitMessage(result.message || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      setSubmitMessage('Failed to send message. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      name: '',
      email: '',
      projectCode: '',
      message: ''
    });
    setErrors({});
    setSubmitMessage('');
  };

  const handleClose = () => {
    handleReset();
    if (onClose) onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        handleClose();
      }}
      maxWidth="md"
      fullWidth
      aria-labelledby="contact-dialog-title"
      PaperProps={{
        sx: { 
          minHeight: '500px',
          borderTop: '3px solid #003087'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0',
        pb: 2
      }}>
        <Typography variant="h5" id="contact-dialog-title" sx={{ color: '#003087', fontWeight: 700 }}>
          Contact Us
        </Typography>
        <IconButton onClick={handleClose} aria-label="Close contact form dialog" size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, backgroundColor: '#fafafa' }}>
        {Object.keys(errors).length > 0 && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Validation Error
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Please correct the following error(s) before proceeding:-
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {Object.values(errors).map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </Box>
          </Alert>
        )}

        {submitMessage && (
          <Alert 
            severity={submitMessage.includes('sent') ? 'success' : 'error'} 
            sx={{ mb: 2, borderRadius: 1 }}
          >
            {submitMessage}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 30%', minWidth: '200px' }}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                error={!!errors.name}
                helperText={errors.name}
                required
                inputProps={{ maxLength: 50 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    '&:hover fieldset': { borderColor: '#003087' },
                    '&.Mui-focused fieldset': { borderColor: '#003087' }
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: '1 1 30%', minWidth: '200px' }}>
              <TextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                error={!!errors.email}
                helperText={errors.email}
                required
                inputProps={{ maxLength: 50 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    '&:hover fieldset': { borderColor: '#003087' },
                    '&.Mui-focused fieldset': { borderColor: '#003087' }
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: '1 1 30%', minWidth: '200px' }}>
              <TextField
                fullWidth
                label="Project Code"
                name="projectCode"
                value={formData.projectCode}
                onChange={handleInputChange}
                inputProps={{ maxLength: 8 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    '&:hover fieldset': { borderColor: '#003087' },
                    '&.Mui-focused fieldset': { borderColor: '#003087' }
                  }
                }}
              />
            </Box>
          </Box>
          <Box>
            <TextField
              fullWidth
              label="Message"
              name="message"
              multiline
              rows={10}
              value={formData.message}
              onChange={handleInputChange}
              error={!!errors.message}
              helperText={errors.message}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  '&:hover fieldset': { borderColor: '#003087' },
                  '&.Mui-focused fieldset': { borderColor: '#003087' }
                }
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: '#f5f5f5', borderTop: '1px solid #e0e0e0', gap: 1 }}>
        <Button 
          onClick={handleReset} 
          disabled={isSubmitting}
          aria-label="Clear contact form"
          sx={{ 
            color: '#333',
            border: '1px solid #ccc',
            '&:hover': { backgroundColor: '#e0e0e0' }
          }}
        >
          Clear
        </Button>
        <Button 
          onClick={handleClose} 
          disabled={isSubmitting}
          aria-label="Cancel and close contact form"
          sx={{ 
            color: '#333',
            border: '1px solid #ccc',
            '&:hover': { backgroundColor: '#e0e0e0' }
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={isSubmitting}
          aria-label={isSubmitting ? 'Sending message' : 'Send contact message'}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
          sx={{
            backgroundColor: '#003087',
            '&:hover': { backgroundColor: '#002060' },
            '&:disabled': { backgroundColor: '#ccc' }
          }}
        >
          {isSubmitting ? 'Sending...' : 'Send Mail'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactUs;